import { createFileRoute } from "@tanstack/react-router";
import { PageContent } from "@/components/layout/page-content";
import { CreateSubscriptionModal } from "@/components/modals/create-subscription-modal";
import { PayInvoiceModal } from "@/components/modals/pay-invoice-modal";
import { UpdatePaymentMethodModal } from "@/components/modals/update-payment-method-modal";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Spinner } from "@/components/ui/spinner";
import {
	BillingIcon,
	BillingMain,
} from "@/features/billing/components/billing-main";
import { useBillingPage } from "@/features/billing/hooks/use-billing-page";

type BillingSearch = {
	branch?: string;
	charge_status?: string;
};

export const Route = createFileRoute("/(dashboard)/settings/billing")({
	component: BillingPage,
	validateSearch: (search: Record<string, unknown>): BillingSearch => ({
		branch: typeof search.branch === "string" ? search.branch : undefined,
		charge_status:
			typeof search.charge_status === "string"
				? search.charge_status
				: undefined,
	}),
});

function BillingPage() {
	const billing = useBillingPage();

	const existingPlan =
		billing.subscription?.plan === "monthly" ||
		billing.subscription?.plan === "yearly"
			? billing.subscription.plan
			: undefined;

	function renderContent() {
		if (billing.isLoadingBranches) {
			return (
				<div className="grid place-content-center h-full">
					<p className="text-muted-foreground text-sm flex items-center gap-2">
						<Spinner />
						<span>Loading branches...</span>
					</p>
				</div>
			);
		}

		if (billing.branches.length === 0) {
			return (
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant="icon">
							<BillingIcon />
						</EmptyMedia>
						<EmptyTitle>No branches</EmptyTitle>
						<EmptyDescription>
							Create a branch first to manage billing.
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			);
		}

		return (
			<ErrorBoundary
				fallback={
					<p className="text-sm text-muted-foreground py-8 text-center">
						Failed to load billing. Please refresh the page.
					</p>
				}
			>
				<BillingMain billing={billing} />
			</ErrorBoundary>
		);
	}

	return (
		<PageContent title="Billing">
			{renderContent()}

			<CreateSubscriptionModal
				open={billing.createModalOpen}
				onOpenChange={billing.setCreateModalOpen}
				onCreateSubscription={billing.handleCreateSubscription}
				onPayFirstInvoice={billing.handlePayFirstInvoice}
				subscriptionStatus={billing.subscription?.status}
				existingSubscriptionId={billing.subscription?._id}
				existingPlan={existingPlan}
				isSubmitting={billing.isCreating || billing.isPayingFirst}
				planPrices={billing.planPrices}
			/>

			<PayInvoiceModal
				open={billing.payModalOpen}
				onOpenChange={billing.setPayModalOpen}
				invoice={billing.selectedInvoice}
				invoiceStatus={billing.selectedInvoiceLive?.status}
				onPay={billing.handlePay}
				isSubmitting={billing.isPayingInvoice}
			/>

			<UpdatePaymentMethodModal
				open={billing.paymentMethodModalOpen}
				onOpenChange={billing.setPaymentMethodModalOpen}
				hasExistingCard={Boolean(billing.subscription?.omiseCustomerId)}
				onUpdate={billing.handleUpdatePaymentMethod}
			/>
		</PageContent>
	);
}
